import {
  AtMedia,
  AtText,
  AtHeader,
  AtVideoPlayer,
  AtDivider,
  AtClickable,
  IAtClickableProps,
} from '@/components/atoms';
import { ETextVariants } from '@/components/atoms/at-text/at-text.types';
import {
  EClickableSize,
  EClickableTarget,
  EClickableTypes,
  EClickableVariants,
} from '@/components/atoms/at-clickable/at-clickable.types';
import { MlCopySection } from './ml-copy-section';
import { EThemes } from '@/lib/contentstack-sdk/types';
import { RenderRichText } from '@/components/helpers/cms/RenderRichText';
import { parseImageFromCMS } from '@/lib/contentstack-sdk/utils/parseHelpers';
import {
  TMlCopySectionBodyText,
  TMlCopySectionBodyImage,
  TMlCopySectionBodyVideo,
  TMlCopySectionBodyQuote,
  TMlCopySectionBodyEndBlock,
  TMlCopySectionBodyProps,
  IMlCopySectionCSProps,
} from './ml-copy-section.types';
import {
  EDividerDirectionalVariants,
  EDividerColors,
  EDividerVariants,
} from '@/components/atoms/at-divider/at-divider.types';

function isTextProps(obj: any): obj is TMlCopySectionBodyText {
  return 'text' in obj;
}

function isImageProps(obj: any): obj is TMlCopySectionBodyImage {
  return 'image' in obj;
}

function isVideoProps(obj: any): obj is TMlCopySectionBodyVideo {
  return 'youtube_embed' in obj;
}

function isQuoteProps(obj: any): obj is TMlCopySectionBodyQuote {
  return 'quote' in obj;
}

function isEndBlockProps(obj: any): obj is TMlCopySectionBodyEndBlock {
  return 'end_block' in obj;
}

export const RenderChildren = ({
  content,
}: {
  content: TMlCopySectionBodyProps;
}) => {
  if (isTextProps(content)) {
    const {
      text: { text_content, end_link },
    } = content;

    return (
      <span className="rtCopySection">
        <RenderRichText body={text_content} useProse={false} />
        {end_link?.link?.href && (
          <AtClickable
            className="pt-4 lg:pt-6"
            type={EClickableTypes.LINK}
            variant={
              end_link.variant
                ? EClickableVariants[
                    end_link.variant as keyof typeof EClickableVariants
                  ]
                : EClickableVariants.PRIMARY
            }
            href={end_link?.link?.href}
            interactLocation="ml-copy-section"
          >
            {end_link.link?.title}
          </AtClickable>
        )}
      </span>
    );
  }

  if (isImageProps(content)) {
    const imageProps = parseImageFromCMS(content.image);

    return (
      <div className="mb-12 mt-15 aspect-[1/1.2] w-full md:aspect-[12/9] lg:mb-20 lg:mt-12">
        {imageProps?.src && <AtMedia {...imageProps} />}
        <figcaption className="flex justify-center pt-4">
          {content.image.caption}
        </figcaption>
      </div>
    );
  }

  if (isVideoProps(content)) {
    return (
      <div className="relative my-12 aspect-video overflow-hidden rounded-18 lg:mb-20 lg:mt-12">
        <AtVideoPlayer url={content?.youtube_embed.url} />
      </div>
    );
  }

  if (isQuoteProps(content)) {
    const { quote_text, quote_attribution } = content?.quote;
    return (
      <div className="my-12">
        <AtDivider
          direction={EDividerDirectionalVariants.HORIZONTAL}
          color={EDividerColors.GREY}
          intent={EDividerVariants.THIN}
        />
        <AtHeader
          element="p"
          variant="h3"
          className="h3-quote-sm py-6 lg:py-12"
        >
          {quote_text}
        </AtHeader>
        <AtText size="lg" className="text-skin-tertiary">
          <RenderRichText body={quote_attribution} useProse={false} />
        </AtText>
        <AtDivider
          direction={EDividerDirectionalVariants.HORIZONTAL}
          color={EDividerColors.GREY}
          intent={EDividerVariants.THIN}
          customClasses="mt-6 lg:mt-12"
        />
      </div>
    );
  }

  if (isEndBlockProps(content)) {
    const { eyebrow, title, title_element, description, link } =
      content.end_block;
    return (
      <div className="mt-15">
        <AtDivider
          direction={EDividerDirectionalVariants.HORIZONTAL}
          color={EDividerColors.GREY}
          intent={EDividerVariants.THIN}
        />
        <div className="flex flex-col gap-4 py-6 pr-6 lg:gap-6">
          <AtText variant={ETextVariants.LABEL} className="text-skin-tertiary">
            {eyebrow}
          </AtText>
          <AtHeader element={title_element} alt variant="h3">
            {title}
          </AtHeader>
          <AtText>{description}</AtText>
          <AtClickable
            className="not-prose w-fit"
            type={EClickableTypes.LINK}
            variant={EClickableVariants.PRIMARY}
            href={link?.href}
            interactLocation="interactLocation"
          >
            {link.title}
          </AtClickable>
        </div>
      </div>
    );
  }
};

export const MlCopySectionCS = ({
  body,
  cards,
  top_spacing,
  bottom_spacing,
}: IMlCopySectionCSProps) => {
  const cardProps = cards?.map((card) => {
    return {
      title: card.card.title,
      body: card.card.description,
      theme: EThemes[card.card.theme],
      ctaProps: {
        size: EClickableSize[
          card.card.link.size as keyof typeof EClickableSize
        ],
        href: card.card.link.link.href,
        target:
          EClickableTarget[
            card.card.link.target as keyof typeof EClickableTarget
          ],
        variant:
          EClickableVariants[
            card.card.link.variant as keyof typeof EClickableVariants
          ],
        outline: card.card.link.outline,
        type: EClickableTypes[
          card.card.link.type as keyof typeof EClickableTypes
        ],
        label: card.card.link.link.title,
      } as IAtClickableProps,
    };
  });

  return (
    <MlCopySection
      cardProps={cardProps}
      bottomDivider={bottom_spacing?.divider}
      topDivider={top_spacing?.divider}
      topSpacing={top_spacing?.spacing_value}
      bottomSpacing={bottom_spacing?.spacing_value}
    >
      {body?.map((cont: TMlCopySectionBodyProps, idx) => {
        return <RenderChildren content={cont} key={idx} />;
      })}
    </MlCopySection>
  );
};
